











































































import { computed, defineComponent, PropType, ref, watch } from "@vue/composition-api";
import { debounce, throttle } from "lodash-es";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import {
  AppointmentKindSettings,
  AppointmentType,
  AppointmentTypeKind,
  DayOfWeek,
  InputAppointmentType,
} from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentTypeProvider";
import { AvailabilityIntervalType } from "@scrile/api-provider/dist/projects/scrileConnect/UserAvailabilityProvider";
import useAuthUser from "@/hooks/useAuthUser";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { Tab } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseSegmentedButton from "@/components/BaseSegmentedButton";
import BaseSwitch from "@/components/BaseSwitch";
import AppointmentTypeItemSettings from "./components/AppointmentTypeItemSettings";
import DisableAdvancedModeConfirm from "./components/DisableAdvancedModeConfirm";
import { daysOfWeekMap, daysOfWeekTabs, onlineAppointmentKinds, weekStart } from "./config";

export default defineComponent({
  name: "AppointmentTypesSettings",
  components: { DisableAdvancedModeConfirm, BaseSwitch, BaseSegmentedButton, BaseButton, AppointmentTypeItemSettings },
  props: {
    manualSave: { type: Boolean },
    activeType: { type: String as PropType<AvailabilityIntervalType>, required: true },
  },
  setup(props, { emit }) {
    const { paymentPackages, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.APPOINTMENT]);
    const isOfflineSettings = computed(() => props.activeType === AvailabilityIntervalType.TOUR_GUIDE);
    const activeAppointmentKinds = computed(() =>
      isOfflineSettings.value ? [AppointmentTypeKind.TOUR_GUIDE] : onlineAppointmentKinds
    );

    const { authUserStrict } = useAuthUser();
    const appointmentKinds = computed<Tab[]>(() => [
      {
        text: $t("Chat"),
        value: AppointmentTypeKind.MESSAGING,
      },
      {
        text: $t("Video"),
        value: AppointmentTypeKind.VIDEO_CALLS,
      },
      {
        text: $t("Audio"),
        value: AppointmentTypeKind.AUDIO_CALLS,
      },
    ]);

    const subscribersTabs = computed<Tab[]>(() => [
      {
        text: $t("Non-subs"),
        value: false,
      },
      {
        text: $t("Subs"),
        value: true,
      },
    ]);

    const currentKind = ref<AppointmentTypeKind>(
      isOfflineSettings.value ? AppointmentTypeKind.TOUR_GUIDE : AppointmentTypeKind.MESSAGING
    );
    const advancedSettings = ref<AppointmentKindSettings[]>([]);

    const advancedEnabled = computed(() => {
      if (isOfflineSettings.value) {
        return (
          advancedSettings.value.find(setting => setting.kind === AppointmentTypeKind.TOUR_GUIDE)?.advancedMode ?? false
        );
      }
      return advancedSettings.value.some(
        setting => onlineAppointmentKinds.includes(setting.kind) && setting.advancedMode
      );
    });

    const showAdvancedModeDisableConfirm = ref(false);

    const forSubscribers = ref(false);
    const currentDayOfWeek = ref<DayOfWeek>(weekStart);

    const appointments = ref<AppointmentType[]>([]);

    const advancedTitleText = computed(() => {
      const dayName = daysOfWeekMap.value[currentDayOfWeek.value].fullName;
      return $t(forSubscribers.value ? "For subs on {0}" : "For non-subs on {0}", [dayName]);
    });

    const fetchTypes = async () => {
      const result = await providers.AppointmentTypeProvider.search({
        data: { userIds: [authUserStrict.value.id] },
      });
      appointments.value = result.find(i => i.userId === authUserStrict.value.id)?.types ?? [];
      emit("setValid", !!appointments.value.length);
    };

    const fetchKindSettings = async () => {
      advancedSettings.value = await providers.AppointmentTypeProvider.searchKindSettings({
        userId: authUserStrict.value.id,
      });
    };

    const updateAdvancedMode = async (data: AppointmentKindSettings[]) => {
      const promises: Promise<void>[] = [];
      data.forEach(setting => {
        promises.push(
          providers.AppointmentTypeProvider.updateKindSettings({ data: setting }).then(response => {
            const index = advancedSettings.value.findIndex(item => item.kind === response.kind);
            if (~index) {
              advancedSettings.value.splice(index, 1, response);
            } else {
              advancedSettings.value.push(response);
            }
          })
        );
      });
      await Promise.all(promises);
    };

    const updateTypes = throttle(async (types: AppointmentType[]) => {
      const result = await providers.AppointmentTypeProvider.update({
        data: {
          userId: authUserStrict.value.id,
          types: types.map<InputAppointmentType>(t => ({
            kind: t.kind,
            duration: t.duration,
            pricePackageId: t.pricePackageId,
            dayOfWeek: t.dayOfWeek,
            forSubscribers: t.forSubscribers,
          })),
        },
      });
      appointments.value = result.types;
    }, 500);

    const manualUpdateTypes = (types: AppointmentType[]) => {
      emit("setTypes", types);
      emit("setValid", !!types.length);
    };

    const manualUpdateAdvancedMode = (settings: AppointmentKindSettings[]) => {
      emit("setAdvancedMode", settings);
      settings.forEach(setting => {
        const index = advancedSettings.value.findIndex(item => item.kind === setting.kind);
        if (~index) {
          advancedSettings.value.splice(index, 1, setting);
        } else {
          advancedSettings.value.push(setting);
        }
      });
    };

    const populateEmptyAdvancedTypes = (kinds: AppointmentTypeKind[]) => {
      kinds.forEach(kind => {
        const kindAppointments = appointments.value.filter(item => item.kind === kind);
        if (kindAppointments.length && kindAppointments.every(item => item.dayOfWeek === DayOfWeek.ALL)) {
          const advancedAppointments: AppointmentType[] = [];
          kindAppointments.forEach(item => {
            Object.keys(DayOfWeek)
              .filter(day => day !== "ALL")
              .forEach(day => {
                advancedAppointments.push(
                  {
                    ...item,
                    id: `non-sub-${day}-${item.kind}`,
                    dayOfWeek: DayOfWeek[day as keyof typeof DayOfWeek],
                  },
                  {
                    ...item,
                    id: `sub-${day}-${item.kind}`,
                    forSubscribers: true,
                    dayOfWeek: DayOfWeek[day as keyof typeof DayOfWeek],
                  }
                );
              });
          });
          appointments.value = appointments.value.concat(advancedAppointments);
        }
      });
      props.manualSave ? manualUpdateTypes(appointments.value) : updateTypes(appointments.value);
    };

    const checkAdvancedModeChanged = () => {
      const simpleAppointments: string[] = [];
      const nonSubsAppointmentsPerDay: Record<Exclude<DayOfWeek, "ALL">, string[]> = {
        [DayOfWeek.MONDAY]: [],
        [DayOfWeek.TUESDAY]: [],
        [DayOfWeek.WEDNESDAY]: [],
        [DayOfWeek.THURSDAY]: [],
        [DayOfWeek.FRIDAY]: [],
        [DayOfWeek.SATURDAY]: [],
        [DayOfWeek.SUNDAY]: [],
      };
      const subsAppointmentsPerDay: Record<Exclude<DayOfWeek, "ALL">, string[]> = {
        [DayOfWeek.MONDAY]: [],
        [DayOfWeek.TUESDAY]: [],
        [DayOfWeek.WEDNESDAY]: [],
        [DayOfWeek.THURSDAY]: [],
        [DayOfWeek.FRIDAY]: [],
        [DayOfWeek.SATURDAY]: [],
        [DayOfWeek.SUNDAY]: [],
      };
      appointments.value.forEach(item => {
        const appointmentKey = `${item.duration}${item.kind}${item.pricePackageId}`;
        if (item.dayOfWeek === DayOfWeek.ALL) {
          simpleAppointments.push(appointmentKey);
        } else {
          item.forSubscribers
            ? subsAppointmentsPerDay[item.dayOfWeek].push(appointmentKey)
            : nonSubsAppointmentsPerDay[item.dayOfWeek].push(appointmentKey);
        }
      });

      const days = Object.values(nonSubsAppointmentsPerDay).concat(Object.values(subsAppointmentsPerDay));
      for (const day of days) {
        if (day.length !== simpleAppointments.length) {
          return true;
        }

        if (day.some(appointment => !simpleAppointments.includes(appointment))) {
          return true;
        }
      }
      return false;
    };

    const toggleAdvancedMode = debounce(async (value: boolean) => {
      const data = activeAppointmentKinds.value.map(kind => ({ kind, advancedMode: value }));
      if (value) {
        props.manualSave ? manualUpdateAdvancedMode(data) : await updateAdvancedMode(data);
        populateEmptyAdvancedTypes(activeAppointmentKinds.value);
      } else {
        if (checkAdvancedModeChanged()) {
          showAdvancedModeDisableConfirm.value = true;
        } else {
          props.manualSave ? manualUpdateAdvancedMode(data) : await updateAdvancedMode(data);
        }
      }
    }, 500);

    const disableAdvancedMode = async () => {
      const data = activeAppointmentKinds.value.map(kind => ({ kind, advancedMode: false }));
      props.manualSave ? manualUpdateAdvancedMode(data) : await updateAdvancedMode(data);
      showAdvancedModeDisableConfirm.value = false;
    };

    const groupedAppointments = computed(() => {
      const result: Record<AppointmentTypeKind, AppointmentType[]> = {
        [AppointmentTypeKind.MESSAGING]: [],
        [AppointmentTypeKind.VIDEO_CALLS]: [],
        [AppointmentTypeKind.AUDIO_CALLS]: [],
        [AppointmentTypeKind.TOUR_GUIDE]: [],
      };
      appointments.value.forEach(appointment => {
        result[appointment.kind].push(appointment);
      });
      return result;
    });

    const currentAppointments = computed(() => {
      return groupedAppointments.value[currentKind.value].filter(item =>
        advancedEnabled.value
          ? item.dayOfWeek === currentDayOfWeek.value && item.forSubscribers === forSubscribers.value
          : item.dayOfWeek === DayOfWeek.ALL
      );
    });

    const selectedDurations = computed(() => currentAppointments.value.map(i => i.duration));

    const showAddPriceButton = ref(true);
    const onEmptyDuration = (status: boolean) => {
      showAddPriceButton.value = !status;
    };

    const onAddType = async () => {
      appointments.value.push({
        id: "-" + appointments.value.length,
        duration: 0,
        pricePackageId: paymentPackages.value[0]?.id,
        kind: currentKind.value,
        dayOfWeek: advancedEnabled.value ? currentDayOfWeek.value : DayOfWeek.ALL,
        forSubscribers: advancedEnabled.value ? forSubscribers.value : false,
      });
    };

    const onUpdate = (data: { id: AppointmentType["id"]; duration: number; pricePackageId: PaymentPackage["id"] }) => {
      appointments.value = appointments.value.map(a => {
        if (a.id !== data.id) {
          return a;
        }
        return {
          ...a,
          duration: data.duration,
          pricePackageId: data.pricePackageId,
        };
      });
      props.manualSave ? manualUpdateTypes(appointments.value) : updateTypes(appointments.value);
    };

    const onRemove = (id: AppointmentType["id"]) => {
      appointments.value = appointments.value.filter(a => a.id !== id);
      props.manualSave ? manualUpdateTypes(appointments.value) : updateTypes(appointments.value);
    };

    const init = () => {
      emit("setValid", false);
      emit("setCb", (data: AppointmentType[]) => updateTypes(data));
      emit("setAdvancedCb", (data: AppointmentKindSettings[]) => updateAdvancedMode(data));
      Promise.all([fetchTypes(), getPaymentPackages(), fetchKindSettings()]);
    };

    init();

    watch(isOfflineSettings, () => {
      currentKind.value = isOfflineSettings.value ? AppointmentTypeKind.TOUR_GUIDE : AppointmentTypeKind.MESSAGING;
    });

    watch(currentKind, () => {
      showAddPriceButton.value = true;
    });

    return {
      advancedEnabled,
      showAdvancedModeDisableConfirm,
      daysOfWeekTabs,
      currentDayOfWeek,
      forSubscribers,
      subscribersTabs,
      appointmentKinds,
      currentKind,
      currentAppointments,
      selectedDurations,
      isOfflineSettings,
      advancedTitleText,
      onAddType,
      paymentPackages,
      onRemove,
      onUpdate,
      showAddPriceButton,
      onEmptyDuration,
      toggleAdvancedMode,
      disableAdvancedMode,
    };
  },
});
